import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CookieBanner from "@/components/CookieBanner";
import ChatWidget from "@/components/ChatWidget";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Realisations from "@/pages/Realisations";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminPhotos from "@/pages/AdminPhotos";
import SeoLocalPage from "@/pages/SeoLocalPage";
import ZonesIntervention from "@/pages/ZonesIntervention";
import MentionsLegales from "@/pages/MentionsLegales";
import DemandeDevis from "@/pages/DemandeDevis";
import NotreProcessus from "@/pages/NotreProcessus";
import AdminBeforeAfter from "@/pages/AdminBeforeAfter";
import NotFound from "@/pages/NotFound";
import { seoPages } from "@/data/seoPages";
import { resolveRedirect } from "@/data/redirects";
import { MessageCircle } from "lucide-react";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function WhatsAppButton() {
  const msg = encodeURIComponent("Bonjour, je suis intéressé par vos services VGI Construct");
  return (
    <a href={`https://wa.me/32466407988?text=${msg}`} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-float"
      aria-label="Contactez-nous sur WhatsApp"
      style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9998, width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(37,211,102,0.4)', transition: 'transform 0.3s, box-shadow 0.3s', cursor: 'pointer', textDecoration: 'none' }}
      onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(37,211,102,0.5)'; }}
      onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,211,102,0.4)'; }}>
      <MessageCircle size={28} color="#fff" fill="#fff" />
    </a>
  );
}

function AppLayout({ children, hideLayout }) {
  if (hideLayout) return children;
  return (
    <>
      <Header />
      <main className="min-h-screen">{children}</main>
      <Footer />
      <WhatsAppButton />
      <ChatWidget />
      <CookieBanner />
    </>
  );
}

// Anciennes URLs WordPress → 301 côté client, sinon vraie page 404
function CatchAll() {
  const { pathname, search } = useLocation();
  const target = resolveRedirect(pathname, search);
  if (target) return <Navigate to={target} replace />;
  return <NotFound />;
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <AppLayout hideLayout={isAdmin}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/a-propos" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/realisations" element={<Realisations />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/zones-intervention" element={<ZonesIntervention />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/demande-devis" element={<DemandeDevis />} />
        <Route path="/notre-processus" element={<NotreProcessus />} />
        <Route path="/404" element={<NotFound />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/photos" element={<AdminPhotos />} />
        <Route path="/admin/before-after" element={<AdminBeforeAfter />} />
        {Object.keys(seoPages).map(slug => (
          <Route key={slug} path={`/travaux-${slug}`} element={<SeoLocalPage slug={slug} />} />
        ))}
        <Route path="*" element={<CatchAll />} />
      </Routes>
    </AppLayout>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
